import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, Printer, RefreshCw } from 'lucide-react';

function csvEscape(val) {
  const s = val == null ? '' : String(val);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename, headerRow, dataRows) {
  const lines = [headerRow.map(csvEscape).join(',')];
  dataRows.forEach((row) => lines.push(row.map(csvEscape).join(',')));
  const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function monthStartISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function formatDate(d) {
  if (!d) return '';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '';
  return x.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function monthKey(d) {
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '';
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(key) {
  const [y, m] = key.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
}

function BarChart({ title, rows, valuePrefix = '' }) {
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <div className="jet-report-chart">
      <h3 className="jet-report-chart__title">{title}</h3>
      {rows.length === 0 ? (
        <p style={{ color: 'var(--jet-muted)', fontSize: '0.9rem' }}>No data for this period.</p>
      ) : (
        <ul className="jet-report-chart__bars">
          {rows.map((row) => (
            <li key={row.label} className="jet-report-chart__row">
              <span className="jet-report-chart__label">{row.label}</span>
              <div className="jet-report-chart__track">
                <div
                  className="jet-report-chart__fill"
                  style={{ width: `${Math.max(4, (row.value / max) * 100)}%` }}
                />
              </div>
              <span className="jet-report-chart__value">
                {valuePrefix}
                {typeof row.value === 'number' ? row.value.toFixed(row.decimals ?? 0) : row.value}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminReportsPanel({ apiBase, authToken, isActive }) {
  const [dateFrom, setDateFrom] = useState(monthStartISO);
  const [dateTo, setDateTo] = useState(todayISO);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [collections, setCollections] = useState([]);
  const [financeSummary, setFinanceSummary] = useState(null);
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [shipments, setShipments] = useState([]);

  const headers = useMemo(
    () => ({
      'Content-Type': 'application/json',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    }),
    [authToken],
  );

  const load = useCallback(async () => {
    if (!authToken || !isActive) return;
    setLoading(true);
    setError('');
    try {
      const q = `from=${encodeURIComponent(dateFrom)}&to=${encodeURIComponent(dateTo)}`;
      const [colRes, sumRes, incRes, expRes, shipRes] = await Promise.all([
        fetch(`${apiBase}/api/collections`, { headers }),
        fetch(`${apiBase}/api/finance/summary?${q}`, { headers }),
        fetch(`${apiBase}/api/finance/transactions?${q}&type=income`, { headers }),
        fetch(`${apiBase}/api/finance/transactions?${q}&type=expense`, { headers }),
        fetch(`${apiBase}/api/shipments?limit=500`, { headers }),
      ]);
      const colJson = await colRes.json().catch(() => ({}));
      const sumJson = await sumRes.json().catch(() => ({}));
      const incJson = await incRes.json().catch(() => ({}));
      const expJson = await expRes.json().catch(() => ({}));
      const shipJson = await shipRes.json().catch(() => ({}));
      if (!colRes.ok) throw new Error(colJson.message || 'Failed to load collections');
      setCollections(Array.isArray(colJson) ? colJson : colJson.data || []);
      if (sumRes.ok) setFinanceSummary(sumJson.data || null);
      if (incRes.ok) setIncomes(incJson.data || []);
      if (expRes.ok) setExpenses(expJson.data || []);
      if (shipRes.ok) setShipments(shipJson.data || []);
    } catch (e) {
      setError(e.message || 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [apiBase, authToken, dateFrom, dateTo, headers, isActive]);

  useEffect(() => {
    load();
  }, [load]);

  const monthlyRevenue = useMemo(() => {
    const map = new Map();
    incomes.forEach((row) => {
      const k = monthKey(row.transactionDate);
      if (!k) return;
      map.set(k, (map.get(k) || 0) + (Number(row.amount) || 0));
    });
    return [...map.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([k, value]) => ({ label: monthLabel(k), value }));
  }, [incomes]);

  const monthlyExpenses = useMemo(() => {
    const map = new Map();
    expenses.forEach((row) => {
      const k = monthKey(row.transactionDate);
      if (!k) return;
      map.set(k, (map.get(k) || 0) + (Number(row.amount) || 0));
    });
    return [...map.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([k, value]) => ({ label: monthLabel(k), value }));
  }, [expenses]);

  const shipmentStatusChart = useMemo(() => {
    const map = new Map();
    shipments.forEach((s) => {
      const status = String(s.status || 'unknown').replace(/-/g, ' ');
      map.set(status, (map.get(status) || 0) + 1);
    });
    return [...map.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([label, value]) => ({ label, value, decimals: 0 }));
  }, [shipments]);

  const slug = `${dateFrom}_to_${dateTo}`;

  const exportCollections = () => {
    downloadCsv(
      `collections-report-${slug}.csv`,
      ['Collection No', 'Consignee', 'Shipper Passport', 'Status', 'Boxes', 'Total CBM', 'Total Price', 'Created'],
      collections.map((c) => [
        c.collectionNo,
        c.consigneeId || '',
        c.shipperPassportNo || '',
        c.status || '',
        (c.boxes || []).length,
        (c.boxes || []).reduce((s, b) => s + (Number(b.cbm) || 0), 0).toFixed(2),
        (c.boxes || []).reduce((s, b) => s + (Number(b.price) || 0) * (Number(b.quantity) || 1), 0).toFixed(2),
        formatDate(c.createdAt),
      ]),
    );
  };

  const exportFinance = () => {
    downloadCsv(
      `finance-summary-${slug}.csv`,
      ['Metric', 'Amount (RM)'],
      [
        ['Total income', financeSummary?.totalIncome ?? 0],
        ['Total expenses', financeSummary?.totalExpense ?? 0],
        ['Net balance', financeSummary?.netBalance ?? 0],
      ],
    );
    downloadCsv(
      `finance-income-${slug}.csv`,
      ['Date', 'Category', 'Description', 'Amount', 'Payment', 'Reference'],
      incomes.map((r) => [
        formatDate(r.transactionDate),
        r.categoryName,
        r.description,
        r.amount,
        r.paymentMethod,
        r.reference,
      ]),
    );
    downloadCsv(
      `finance-expenses-${slug}.csv`,
      ['Date', 'Category', 'Description', 'Amount', 'Reference'],
      expenses.map((r) => [
        formatDate(r.transactionDate),
        r.categoryName,
        r.description,
        r.amount,
        r.reference,
      ]),
    );
  };

  const exportShipments = () => {
    downloadCsv(
      `shipments-report.csv`,
      ['Tracking', 'Status', 'Service', 'Customer', 'Origin', 'Destination', 'Created'],
      shipments.map((s) => [
        s.trackingNumber,
        s.status,
        s.service?.name || s.service?.type || '',
        s.customer?.name || '',
        [s.origin?.city, s.origin?.country].filter(Boolean).join(', '),
        [s.destination?.city, s.destination?.country].filter(Boolean).join(', '),
        formatDate(s.createdAt),
      ]),
    );
  };

  const printReport = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    const income = Number(financeSummary?.totalIncome) || 0;
    const expense = Number(financeSummary?.totalExpense) || 0;
    const net = Number(financeSummary?.netBalance) || 0;
    w.document.write(`
      <html><head><title>Jet Shipping Report</title>
      <style>body{font-family:system-ui,sans-serif;padding:24px;color:#111}h1{font-size:1.25rem}table{border-collapse:collapse;width:100%;margin-top:12px}td,th{border:1px solid #ddd;padding:8px;text-align:left}th{background:#f1f5f9}</style>
      </head><body>
      <h1>Jet Shipping — Operations Report</h1>
      <p>Period: ${dateFrom} to ${dateTo}</p>
      <h2>Summary</h2>
      <table><tr><th>Collections</th><td>${collections.length}</td></tr>
      <tr><th>Shipments</th><td>${shipments.length}</td></tr>
      <tr><th>Income</th><td>RM ${income.toFixed(2)}</td></tr>
      <tr><th>Expenses</th><td>RM ${expense.toFixed(2)}</td></tr>
      <tr><th>Net</th><td>RM ${net.toFixed(2)}</td></tr></table>
      </body></html>`);
    w.document.close();
    w.print();
  };

  return (
    <div className="jet-card">
      <div className="jet-table-toolbar" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.9rem' }}>
          From
          <input type="date" className="jet-select" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.9rem' }}>
          To
          <input type="date" className="jet-select" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </label>
        <button type="button" className="jet-btn jet-btn-secondary jet-btn-icon" onClick={load} disabled={loading} title="Refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {error ? <p style={{ color: '#dc2626' }}>{error}</p> : null}

      <div className="jet-reports-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
        <div className="jet-card" style={{ padding: '1rem', margin: 0 }}>
          <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem' }}>Collections</h3>
          <p style={{ margin: '0 0 0.75rem', color: 'var(--jet-muted)', fontSize: '0.9rem' }}>{collections.length} records</p>
          <button type="button" className="jet-btn jet-btn-secondary" onClick={exportCollections}>
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>
        <div className="jet-card" style={{ padding: '1rem', margin: 0 }}>
          <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem' }}>Finance</h3>
          <p style={{ margin: '0 0 0.75rem', color: 'var(--jet-muted)', fontSize: '0.9rem' }}>
            Net: RM {(Number(financeSummary?.netBalance) || 0).toFixed(2)}
          </p>
          <button type="button" className="jet-btn jet-btn-secondary" onClick={exportFinance}>
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>
        <div className="jet-card" style={{ padding: '1rem', margin: 0 }}>
          <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem' }}>Shipments</h3>
          <p style={{ margin: '0 0 0.75rem', color: 'var(--jet-muted)', fontSize: '0.9rem' }}>{shipments.length} records</p>
          <button type="button" className="jet-btn jet-btn-secondary" onClick={exportShipments}>
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>
        <div className="jet-card" style={{ padding: '1rem', margin: 0 }}>
          <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem' }}>Print summary</h3>
          <p style={{ margin: '0 0 0.75rem', color: 'var(--jet-muted)', fontSize: '0.9rem' }}>Overview for selected period</p>
          <button type="button" className="jet-btn jet-btn-secondary" onClick={printReport}>
            <Printer className="w-4 h-4" /> Print
          </button>
        </div>
      </div>

      <div className="jet-reports-charts" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem', marginTop: '1.25rem' }}>
        <BarChart title="Monthly income (RM)" rows={monthlyRevenue} valuePrefix="RM " />
        <BarChart title="Monthly expenses (RM)" rows={monthlyExpenses} valuePrefix="RM " />
        <BarChart title="Shipments by status" rows={shipmentStatusChart} />
      </div>
    </div>
  );
}
